import React, { useState, useEffect, useCallback, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faDatabase,
  faTable,
  faColumns,
  faFilter,
  faEye,
  faPaperPlane,
  faShieldAlt,
  faHistory,
  faPlus,
  faTrash,
  faSpinner,
  faCheckCircle,
  faExclamationTriangle,
  faInfoCircle,
  faTimes,
  faSearch,
  faToggleOn,
  faToggleOff,
  faChevronDown,
  faChevronUp,
  faArrowLeft,
  faArrowRight,
  faLock,
  faSyncAlt,
  faUndo,
} from "@fortawesome/free-solid-svg-icons";
import toast from "react-hot-toast";
import Swal from "sweetalert2";
import {
  getAvailableTables,
  getTableColumns,
  previewAffectedRows,
  executeTableUpdate,
  getBlockedTables,
  addBlockedTable,
  removeBlockedTable,
  getAuditLogs,
  revertAuditLog,
} from "../../services/dynamicTableUpdateApi";

// ─────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────
const OPERATORS = [
  { value: "=", label: "= (Equals)" },
  { value: "!=", label: "!= (Not Equals)" },
  { value: ">", label: "> (Greater Than)" },
  { value: "<", label: "< (Less Than)" },
  { value: ">=", label: ">= (Greater or Equal)" },
  { value: "<=", label: "<= (Less or Equal)" },
  { value: "LIKE", label: "LIKE (Pattern)" },
  { value: "NOT LIKE", label: "NOT LIKE" },
  { value: "IS NULL", label: "IS NULL" },
  { value: "IS NOT NULL", label: "IS NOT NULL" },
  { value: "IN", label: "IN (Multiple Values)" },
];

const NO_VALUE_OPERATORS = ["IS NULL", "IS NOT NULL"];

const TABS = [
  { id: "update", label: "Table Update", icon: faTable },
  { id: "blocked", label: "Blocked Tables", icon: faShieldAlt },
  { id: "audit", label: "Audit Logs", icon: faHistory },
];

// ─────────────────────────────────────────────
// Helper: Map SQL data type to input type
// ─────────────────────────────────────────────
function getInputTypeForColumn(col) {
  const dt = (col.data_type || "").toLowerCase();
  const ct = (col.column_type || "").toLowerCase();

  if (dt === "enum" || dt === "set") return "select";
  if (ct === "tinyint(1)" || dt === "boolean") return "boolean";
  if (["int", "bigint", "smallint", "mediumint", "tinyint"].includes(dt)) return "number";
  if (["decimal", "float", "double", "numeric"].includes(dt)) return "decimal";
  if (dt === "date") return "date";
  if (dt === "datetime" || dt === "timestamp") return "datetime-local";
  if (dt === "time") return "time";
  if (dt === "year") return "number";
  if (["text", "mediumtext", "longtext"].includes(dt)) return "textarea";
  if (dt === "json") return "textarea";
  return "text";
}

// ─────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────
const DynamicTableUpdatePage = () => {
  // Tab state
  const [activeTab, setActiveTab] = useState("update");

  // Step wizard state
  const [currentStep, setCurrentStep] = useState(1);

  // Tables
  const [tables, setTables] = useState([]);
  const [tablesLoading, setTablesLoading] = useState(false);
  const [tableSearch, setTableSearch] = useState("");
  const [selectedTable, setSelectedTable] = useState(null);

  // Columns
  const [columns, setColumns] = useState([]);
  const [primaryKeys, setPrimaryKeys] = useState([]);
  const [columnsLoading, setColumnsLoading] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState([]);

  // Column values (the new values to set)
  const [columnValues, setColumnValues] = useState({});
  const [nullFlags, setNullFlags] = useState({});

  // Filters
  const [filters, setFilters] = useState([{ column: "", operator: "=", value: "" }]);
  const [updateMode, setUpdateMode] = useState("single");

  // Preview
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Execute
  const [executeLoading, setExecuteLoading] = useState(false);

  // Column search
  const [columnSearch, setColumnSearch] = useState("");

  // Blocked tables tab
  const [blockedTables, setBlockedTables] = useState({ hardcoded_blocked: [], configurable_blocked: [] });
  const [blockedLoading, setBlockedLoading] = useState(false);
  const [newBlockedTable, setNewBlockedTable] = useState("");
  const [newBlockedReason, setNewBlockedReason] = useState("");

  // Audit logs tab
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditPage, setAuditPage] = useState(1);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditFilterTable, setAuditFilterTable] = useState("");
  const [expandedAuditRow, setExpandedAuditRow] = useState(null);
  const [revertLoading, setRevertLoading] = useState(null); // holds the audit log id being reverted

  // ──────────────── Data Fetching ────────────────

  const fetchTables = useCallback(async () => {
    setTablesLoading(true);
    try {
      const res = await getAvailableTables();
      setTables(res.data?.data?.tables || []);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load tables");
    } finally {
      setTablesLoading(false);
    }
  }, []);

  const fetchColumns = useCallback(async (tableName) => {
    setColumnsLoading(true);
    try {
      const res = await getTableColumns(tableName);
      const data = res.data?.data || {};
      setColumns(data.columns || []);
      setPrimaryKeys(data.primary_keys || []);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load columns");
    } finally {
      setColumnsLoading(false);
    }
  }, []);

  const fetchBlockedTables = useCallback(async () => {
    setBlockedLoading(true);
    try {
      const res = await getBlockedTables();
      setBlockedTables(res.data?.data || { hardcoded_blocked: [], configurable_blocked: [] });
    } catch (err) {
      toast.error("Failed to load blocked tables");
    } finally {
      setBlockedLoading(false);
    }
  }, []);

  const fetchAuditLogs = useCallback(async (page = 1, targetTable = "") => {
    setAuditLoading(true);
    try {
      const res = await getAuditLogs({ page, per_page: 20, target_table: targetTable || undefined });
      const data = res.data?.data || {};
      setAuditLogs(data.rows || []);
      setAuditTotal(data.total || 0);
    } catch (err) {
      toast.error("Failed to load audit logs");
    } finally {
      setAuditLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === "update") fetchTables();
    if (activeTab === "blocked") fetchBlockedTables();
    if (activeTab === "audit") fetchAuditLogs(1, auditFilterTable);
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // ──────────────── Filtered tables ────────────────
  const filteredTables = useMemo(() => {
    if (!tableSearch.trim()) return tables;
    const q = tableSearch.toLowerCase();
    return tables.filter((t) => t.table_name.toLowerCase().includes(q));
  }, [tables, tableSearch]);

  // ──────────────── Handlers ────────────────

  const handleSelectTable = (table) => {
    setSelectedTable(table);
    setSelectedColumns([]);
    setColumnValues({});
    setNullFlags({});
    setColumnSearch("");
    setFilters([{ column: "", operator: "=", value: "" }]);
    setPreviewData(null);
    setCurrentStep(2);
    fetchColumns(table.table_name);
  };

  const handleToggleColumn = (colName) => {
    setSelectedColumns((prev) =>
      prev.includes(colName) ? prev.filter((c) => c !== colName) : [...prev, colName]
    );
    if (selectedColumns.includes(colName)) {
      setColumnValues((prev) => {
        const next = { ...prev };
        delete next[colName];
        return next;
      });
      setNullFlags((prev) => {
        const next = { ...prev };
        delete next[colName];
        return next;
      });
    }
  };

  const handleColumnValueChange = (colName, value) => {
    setColumnValues((prev) => ({ ...prev, [colName]: value }));
  };

  const handleNullToggle = (colName) => {
    setNullFlags((prev) => {
      const isNow = !prev[colName];
      if (isNow) {
        setColumnValues((pv) => ({ ...pv, [colName]: null }));
      }
      return { ...prev, [colName]: isNow };
    });
  };

  const handleAddFilter = () => {
    setFilters((prev) => [...prev, { column: "", operator: "=", value: "" }]);
  };

  const handleRemoveFilter = (idx) => {
    setFilters((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleFilterChange = (idx, field, value) => {
    setFilters((prev) => prev.map((f, i) => (i === idx ? { ...f, [field]: value } : f)));
  };

  const handlePreview = async () => {
    // Validate
    const validFilters = filters.filter((f) => f.column && f.operator);
    if (validFilters.length === 0) {
      toast.error("At least one filter condition is required.");
      return;
    }
    if (selectedColumns.length === 0) {
      toast.error("Select at least one column to update.");
      return;
    }

    // Build filter payload
    const filterPayload = validFilters.map((f) => ({
      column: f.column,
      operator: f.operator,
      value: f.operator === "IN" ? f.value.split(",").map((v) => v.trim()) : f.value,
    }));

    setPreviewLoading(true);
    try {
      const res = await previewAffectedRows({
        table_name: selectedTable.table_name,
        filters: filterPayload,
        selected_columns: selectedColumns,
      });
      setPreviewData(res.data?.data || null);
      setCurrentStep(4);
    } catch (err) {
      toast.error(err.response?.data?.message || "Preview failed");
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleExecute = async () => {
    // Build values - only for selected columns
    const values = {};
    for (const col of selectedColumns) {
      if (nullFlags[col]) {
        values[col] = null;
      } else {
        values[col] = columnValues[col] ?? "";
      }
    }

    const validFilters = filters.filter((f) => f.column && f.operator);
    const filterPayload = validFilters.map((f) => ({
      column: f.column,
      operator: f.operator,
      value: f.operator === "IN" ? f.value.split(",").map((v) => v.trim()) : f.value,
    }));

    const result = await Swal.fire({
      title: "Confirm Update",
      html: `
        <div style="text-align:left;font-size:14px;">
          <p><strong>Table:</strong> ${selectedTable.table_name}</p>
          <p><strong>Columns:</strong> ${selectedColumns.join(", ")}</p>
          <p><strong>Affected Rows:</strong> ${previewData?.total_affected || "?"}</p>
          <p><strong>Mode:</strong> ${updateMode}</p>
          <br/>
          <p style="color:#dc2626;font-weight:600;">This action cannot be undone. Are you sure?</p>
        </div>
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Execute Update",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc2626",
    });

    if (!result.isConfirmed) return;

    setExecuteLoading(true);
    try {
      const res = await executeTableUpdate({
        table_name: selectedTable.table_name,
        filters: filterPayload,
        column_values: values,
        update_mode: updateMode,
      });
      toast.success(res.data?.message || "Update completed successfully!");
      setCurrentStep(5);
    } catch (err) {
      toast.error(err.response?.data?.message || "Update failed");
    } finally {
      setExecuteLoading(false);
    }
  };

  const handleReset = () => {
    setCurrentStep(1);
    setSelectedTable(null);
    setSelectedColumns([]);
    setColumnValues({});
    setNullFlags({});
    setFilters([{ column: "", operator: "=", value: "" }]);
    setPreviewData(null);
    fetchTables();
  };

  const handleAddBlocked = async () => {
    if (!newBlockedTable.trim()) {
      toast.error("Enter a table name to block.");
      return;
    }
    try {
      await addBlockedTable({ table_name: newBlockedTable.trim(), reason: newBlockedReason.trim() || null });
      toast.success(`Table "${newBlockedTable.trim()}" blocked.`);
      setNewBlockedTable("");
      setNewBlockedReason("");
      fetchBlockedTables();
      fetchTables();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to block table");
    }
  };

  const handleRemoveBlocked = async (id, tableName) => {
    const result = await Swal.fire({
      title: "Remove from blocklist?",
      text: `This will allow "${tableName}" to be updated again.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Unblock",
      confirmButtonColor: "#059669",
    });
    if (!result.isConfirmed) return;
    try {
      await removeBlockedTable(id);
      toast.success(`Table "${tableName}" unblocked.`);
      fetchBlockedTables();
      fetchTables();
    } catch (err) {
      toast.error("Failed to unblock table");
    }
  };

  const handleRevert = async (log) => {
    const result = await Swal.fire({
      title: "Revert this update?",
      html: `<div style="text-align:left;font-size:14px;">
        <p>This will restore <strong>${log.affected_rows} row(s)</strong> in table <code>${log.target_table}</code> back to their <strong>original values</strong> before this update.</p>
        <br/>
        <p style="color:#b91c1c;font-weight:600;">This action cannot be easily undone.</p>
      </div>`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Revert",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc2626",
    });
    if (!result.isConfirmed) return;

    setRevertLoading(log.id);
    try {
      const res = await revertAuditLog(log.id);
      const msg = res.data?.message || "Revert successful.";
      toast.success(msg);
      fetchAuditLogs(auditPage, auditFilterTable);
    } catch (err) {
      const errMsg = err.response?.data?.message || "Revert failed.";
      toast.error(errMsg);
    } finally {
      setRevertLoading(null);
    }
  };

  // ──────────────── Renderers ────────────────

  const renderColumnInput = (col) => {
    const inputType = getInputTypeForColumn(col);
    const colName = col.column_name;
    const isNull = nullFlags[colName];
    const value = columnValues[colName] ?? "";

    const baseClass =
      "w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition disabled:bg-gray-100 disabled:cursor-not-allowed";

    let input;

    switch (inputType) {
      case "select":
        input = (
          <select
            className={baseClass}
            value={value}
            disabled={isNull}
            onChange={(e) => handleColumnValueChange(colName, e.target.value)}
          >
            <option value="">-- Select --</option>
            {(col.enum_values || []).map((ev) => (
              <option key={ev} value={ev}>
                {ev}
              </option>
            ))}
          </select>
        );
        break;
      case "boolean":
        input = (
          <select
            className={baseClass}
            value={value}
            disabled={isNull}
            onChange={(e) => handleColumnValueChange(colName, e.target.value)}
          >
            <option value="">-- Select --</option>
            <option value="1">True (1)</option>
            <option value="0">False (0)</option>
          </select>
        );
        break;
      case "textarea":
        input = (
          <textarea
            className={baseClass + " min-h-[80px] resize-y"}
            value={value || ""}
            disabled={isNull}
            onChange={(e) => handleColumnValueChange(colName, e.target.value)}
            placeholder={`Enter ${colName} value...`}
          />
        );
        break;
      case "number":
        input = (
          <input
            type="number"
            className={baseClass}
            value={value}
            disabled={isNull}
            onChange={(e) => handleColumnValueChange(colName, e.target.value)}
            placeholder="0"
          />
        );
        break;
      case "decimal":
        input = (
          <input
            type="number"
            step="any"
            className={baseClass}
            value={value}
            disabled={isNull}
            onChange={(e) => handleColumnValueChange(colName, e.target.value)}
            placeholder="0.00"
          />
        );
        break;
      case "date":
        input = (
          <input
            type="date"
            className={baseClass}
            value={value}
            disabled={isNull}
            onChange={(e) => handleColumnValueChange(colName, e.target.value)}
          />
        );
        break;
      case "datetime-local":
        input = (
          <input
            type="datetime-local"
            className={baseClass}
            value={value}
            disabled={isNull}
            onChange={(e) => handleColumnValueChange(colName, e.target.value)}
          />
        );
        break;
      case "time":
        input = (
          <input
            type="time"
            className={baseClass}
            value={value}
            disabled={isNull}
            onChange={(e) => handleColumnValueChange(colName, e.target.value)}
          />
        );
        break;
      default:
        input = (
          <input
            type="text"
            className={baseClass}
            value={value || ""}
            disabled={isNull}
            onChange={(e) => handleColumnValueChange(colName, e.target.value)}
            placeholder={`Enter ${colName} value...`}
            maxLength={col.max_length || undefined}
          />
        );
    }

    return (
      <div key={colName} className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
        <div className="flex items-center justify-between mb-2">
          <div>
            <span className="font-semibold text-gray-800 text-sm">{colName}</span>
            <span className="ml-2 text-xs text-gray-400 font-mono">{col.column_type}</span>
            {col.is_primary && (
              <span className="ml-2 px-1.5 py-0.5 text-[10px] font-bold bg-amber-100 text-amber-700 rounded">PK</span>
            )}
          </div>
          {col.is_nullable && (
            <button
              type="button"
              onClick={() => handleNullToggle(colName)}
              className={`flex items-center gap-1 text-xs px-2 py-1 rounded-full transition ${
                isNull
                  ? "bg-orange-100 text-orange-700 hover:bg-orange-200"
                  : "bg-gray-100 text-gray-500 hover:bg-gray-200"
              }`}
            >
              <FontAwesomeIcon icon={isNull ? faToggleOn : faToggleOff} />
              SET NULL
            </button>
          )}
        </div>
        {input}
      </div>
    );
  };

  // ─────────────────────────────────────────────
  // STEP 1: Select Table
  // ─────────────────────────────────────────────
  const renderStep1 = () => (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-bold text-gray-800">Step 1: Select a Table</h3>
          <p className="text-sm text-gray-500 mt-0.5">Choose a database table to update.</p>
        </div>
        <button
          onClick={fetchTables}
          className="flex items-center gap-2 px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition"
        >
          <FontAwesomeIcon icon={faSyncAlt} spin={tablesLoading} />
          Refresh
        </button>
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          placeholder="Search tables..."
          value={tableSearch}
          onChange={(e) => setTableSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
      </div>

      {tablesLoading ? (
        <div className="flex items-center justify-center py-16">
          <FontAwesomeIcon icon={faSpinner} spin className="text-blue-500 text-2xl" />
          <span className="ml-3 text-gray-500">Loading tables...</span>
        </div>
      ) : filteredTables.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <FontAwesomeIcon icon={faDatabase} className="text-4xl mb-3" />
          <p>No tables found.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 max-h-[500px] overflow-y-auto pr-1">
          {filteredTables.map((t) => (
            <button
              key={t.table_name}
              onClick={() => handleSelectTable(t)}
              className={`text-left p-4 rounded-xl border transition hover:shadow-md hover:border-blue-400 group ${
                selectedTable?.table_name === t.table_name
                  ? "border-blue-500 bg-blue-50 shadow-md"
                  : "border-gray-200 bg-white hover:bg-blue-50/50"
              }`}
            >
              <div className="flex items-center gap-2 mb-1">
                <FontAwesomeIcon
                  icon={faTable}
                  className={`text-sm ${
                    selectedTable?.table_name === t.table_name
                      ? "text-blue-600"
                      : "text-gray-400 group-hover:text-blue-500"
                  }`}
                />
                <span className="font-semibold text-sm text-gray-800 truncate">{t.table_name}</span>
              </div>
              <span className="text-xs text-gray-400">~{t.estimated_rows?.toLocaleString() || 0} rows</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );

  // ─────────────────────────────────────────────
  // STEP 2: Select Columns + Set Values
  // ─────────────────────────────────────────────
  const renderStep2 = () => {
    const editableColumns = columns.filter((c) => !c.is_auto_increment);
    const q = columnSearch.toLowerCase().trim();
    const filteredEditableColumns = q
      ? editableColumns.filter(
          (c) =>
            c.column_name.toLowerCase().includes(q) ||
            c.data_type.toLowerCase().includes(q) ||
            (c.column_comment && c.column_comment.toLowerCase().includes(q))
        )
      : editableColumns;

    return (
      <div>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-bold text-gray-800">Step 2: Select Columns &amp; Set Values</h3>
            <p className="text-sm text-gray-500 mt-0.5">
              Table: <span className="font-semibold text-blue-600">{selectedTable?.table_name}</span> &middot;{" "}
              {columns.length} columns available
            </p>
          </div>
        </div>

        {columnsLoading ? (
          <div className="flex items-center justify-center py-16">
            <FontAwesomeIcon icon={faSpinner} spin className="text-blue-500 text-2xl" />
            <span className="ml-3 text-gray-500">Loading columns...</span>
          </div>
        ) : (
          <>
            {/* Column selector */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-semibold text-gray-700">
                  <FontAwesomeIcon icon={faColumns} className="mr-1.5 text-blue-500" />
                  Select columns to update:
                </label>
                {selectedColumns.length > 0 && (
                  <span className="text-xs text-green-600 font-medium">
                    <FontAwesomeIcon icon={faCheckCircle} className="mr-1" />
                    {selectedColumns.length} column(s) selected
                  </span>
                )}
              </div>

              {/* Column search */}
              <div className="relative mb-2">
                <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                <input
                  type="text"
                  placeholder="Search columns by name or data type..."
                  value={columnSearch}
                  onChange={(e) => setColumnSearch(e.target.value)}
                  className="w-full pl-9 pr-8 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                {columnSearch && (
                  <button
                    type="button"
                    onClick={() => setColumnSearch("")}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition"
                  >
                    <FontAwesomeIcon icon={faTimes} className="text-sm" />
                  </button>
                )}
              </div>

              {/* Matching info */}
              {q && (
                <p className="text-xs text-gray-400 mb-1.5">
                  Showing {filteredEditableColumns.length} of {editableColumns.length} columns matching "{columnSearch.trim()}"
                </p>
              )}

              <div className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200 max-h-[240px] overflow-y-auto">
                {filteredEditableColumns.length === 0 ? (
                  <p className="text-sm text-gray-400 py-2 w-full text-center">No columns match your search.</p>
                ) : (
                  filteredEditableColumns.map((col) => {
                    const selected = selectedColumns.includes(col.column_name);
                    return (
                      <button
                        key={col.column_name}
                        type="button"
                        onClick={() => handleToggleColumn(col.column_name)}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium transition border ${
                          selected
                            ? "bg-blue-600 text-white border-blue-600 shadow-sm"
                            : "bg-white text-gray-600 border-gray-300 hover:border-blue-400 hover:text-blue-600"
                        }`}
                      >
                        {col.column_name}
                        <span className="ml-1 opacity-60 font-mono text-[10px]">({col.data_type})</span>
                      </button>
                    );
                  })
                )}
              </div>
            </div>

            {/* Value inputs for selected columns */}
            {selectedColumns.length > 0 && (
              <div className="space-y-3">
                <label className="text-sm font-semibold text-gray-700 block">
                  Set new values for selected columns:
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {selectedColumns.map((colName) => {
                    const col = columns.find((c) => c.column_name === colName);
                    if (!col) return null;
                    return renderColumnInput(col);
                  })}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  // ─────────────────────────────────────────────
  // STEP 3: Set Filters
  // ─────────────────────────────────────────────
  const renderStep3 = () => (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-bold text-gray-800">Step 3: Filter Conditions</h3>
          <p className="text-sm text-gray-500 mt-0.5">
            Define WHERE conditions to target specific rows. At least one filter is required.
          </p>
        </div>
      </div>

      {/* Update mode */}
      <div className="flex items-center gap-4 mb-5 p-3 bg-gray-50 rounded-lg border border-gray-200">
        <span className="text-sm font-medium text-gray-700">Update Mode:</span>
        <label className="flex items-center gap-1.5 cursor-pointer">
          <input
            type="radio"
            name="updateMode"
            value="single"
            checked={updateMode === "single"}
            onChange={() => setUpdateMode("single")}
            className="text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm text-gray-700">Single Record (by PK)</span>
        </label>
        <label className="flex items-center gap-1.5 cursor-pointer">
          <input
            type="radio"
            name="updateMode"
            value="multi"
            checked={updateMode === "multi"}
            onChange={() => setUpdateMode("multi")}
            className="text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm text-gray-700">Multi-Record (filtered)</span>
        </label>
      </div>

      {/* Filter rows */}
      <div className="space-y-3 mb-4">
        {filters.map((filter, idx) => (
          <div key={idx} className="flex items-start gap-2 p-3 bg-white rounded-lg border border-gray-200">
            <select
              value={filter.column}
              onChange={(e) => handleFilterChange(idx, "column", e.target.value)}
              className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
            >
              <option value="">-- Column --</option>
              {columns.map((c) => (
                <option key={c.column_name} value={c.column_name}>
                  {c.column_name} ({c.data_type})
                </option>
              ))}
            </select>

            <select
              value={filter.operator}
              onChange={(e) => handleFilterChange(idx, "operator", e.target.value)}
              className="w-[170px] px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
            >
              {OPERATORS.map((op) => (
                <option key={op.value} value={op.value}>
                  {op.label}
                </option>
              ))}
            </select>

            {!NO_VALUE_OPERATORS.includes(filter.operator) && (
              <input
                type="text"
                value={filter.value || ""}
                onChange={(e) => handleFilterChange(idx, "value", e.target.value)}
                placeholder={filter.operator === "IN" ? "val1, val2, val3" : "Value"}
                className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              />
            )}

            {filters.length > 1 && (
              <button
                type="button"
                onClick={() => handleRemoveFilter(idx)}
                className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
              >
                <FontAwesomeIcon icon={faTrash} />
              </button>
            )}
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={handleAddFilter}
        className="flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800 font-medium transition"
      >
        <FontAwesomeIcon icon={faPlus} />
        Add Filter Condition
      </button>
    </div>
  );

  // ─────────────────────────────────────────────
  // STEP 4: Preview
  // ─────────────────────────────────────────────
  const renderStep4 = () => (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-bold text-gray-800">Step 4: Review &amp; Confirm</h3>
          <p className="text-sm text-gray-500 mt-0.5">Review the pending changes before executing the update.</p>
        </div>
      </div>

      {previewLoading ? (
        <div className="flex items-center justify-center py-16">
          <FontAwesomeIcon icon={faSpinner} spin className="text-blue-500 text-2xl" />
          <span className="ml-3 text-gray-500">Loading preview...</span>
        </div>
      ) : previewData ? (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-center">
              <p className="text-2xl font-bold text-blue-700">{previewData.total_affected}</p>
              <p className="text-xs text-blue-600 font-medium">Rows Affected</p>
            </div>
            <div className="bg-purple-50 border border-purple-200 rounded-xl p-3 text-center">
              <p className="text-2xl font-bold text-purple-700">{selectedColumns.length}</p>
              <p className="text-xs text-purple-600 font-medium">Columns to Update</p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-center">
              <p className="text-2xl font-bold text-amber-700">{filters.filter((f) => f.column).length}</p>
              <p className="text-xs text-amber-600 font-medium">Filter Conditions</p>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-xl p-3 text-center">
              <p className="text-lg font-bold text-green-700 truncate">{updateMode}</p>
              <p className="text-xs text-green-600 font-medium">Update Mode</p>
            </div>
          </div>

          {/* Update details */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4">
            <h4 className="text-sm font-bold text-gray-700 mb-3">New Values to Apply</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {selectedColumns.map((colName) => (
                <div key={colName} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                  <span className="font-mono text-xs text-gray-600 min-w-[120px]">{colName}</span>
                  <FontAwesomeIcon icon={faArrowRight} className="text-gray-400 text-xs" />
                  <span className="text-sm font-semibold text-blue-700">
                    {nullFlags[colName] ? (
                      <span className="text-orange-500 italic">NULL</span>
                    ) : (
                      String(columnValues[colName] ?? "")
                    )}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Filter summary */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4">
            <h4 className="text-sm font-bold text-gray-700 mb-3">Filter Conditions Applied</h4>
            {filters
              .filter((f) => f.column)
              .map((f, i) => (
                <div key={i} className="flex items-center gap-2 text-sm mb-1">
                  <span className="font-mono text-gray-600">{f.column}</span>
                  <span className="text-blue-600 font-semibold">{f.operator}</span>
                  {!NO_VALUE_OPERATORS.includes(f.operator) && (
                    <span className="text-green-700 font-medium">'{f.value}'</span>
                  )}
                </div>
              ))}
          </div>

          {/* Preview table of old values */}
          {previewData.preview_rows?.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-4">
              <h4 className="text-sm font-bold text-gray-700 p-4 pb-2">
                Current Row Data (Before Update) — showing up to {previewData.preview_rows.length} rows
              </h4>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      {previewData.columns_shown?.map((col) => (
                        <th
                          key={col}
                          className="px-3 py-2 text-left font-semibold text-gray-700 text-xs whitespace-nowrap"
                        >
                          {col}
                          {previewData.primary_keys?.includes(col) && (
                            <span className="ml-1 text-amber-500">🔑</span>
                          )}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {previewData.preview_rows.map((row, idx) => (
                      <tr key={idx} className="hover:bg-blue-50/30">
                        {previewData.columns_shown?.map((col) => (
                          <td key={col} className="px-3 py-2 text-gray-700 whitespace-nowrap text-xs font-mono">
                            {row[col] === null ? (
                              <span className="text-gray-400 italic">NULL</span>
                            ) : (
                              String(row[col]).substring(0, 60)
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Warning */}
          {previewData.total_affected > 50 && (
            <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-xl mb-4">
              <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-500 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-red-700">High Impact Warning</p>
                <p className="text-xs text-red-600">
                  This update will affect <strong>{previewData.total_affected} rows</strong>. Please double-check
                  your filters and values before proceeding.
                </p>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-16 text-gray-400">
          <FontAwesomeIcon icon={faEye} className="text-4xl mb-3" />
          <p>No preview data. Go back and click "Preview" first.</p>
        </div>
      )}
    </div>
  );

  // ─────────────────────────────────────────────
  // STEP 5: Success
  // ─────────────────────────────────────────────
  const renderStep5 = () => (
    <div className="text-center py-16">
      <div className="w-20 h-20 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
        <FontAwesomeIcon icon={faCheckCircle} className="text-green-600 text-4xl" />
      </div>
      <h3 className="text-xl font-bold text-gray-800 mb-2">Update Completed Successfully!</h3>
      <p className="text-sm text-gray-500 mb-6">
        The update has been logged in the audit trail.
      </p>
      <button
        onClick={handleReset}
        className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium text-sm"
      >
        Start New Update
      </button>
    </div>
  );

  // ─────────────────────────────────────────────
  // BLOCKED TABLES TAB
  // ─────────────────────────────────────────────
  const renderBlockedTab = () => (
    <div className="space-y-6">
      {/* Hardcoded */}
      <div>
        <h3 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
          <FontAwesomeIcon icon={faLock} className="text-red-500" />
          System-Protected Tables (Cannot Be Changed)
        </h3>
        <div className="flex flex-wrap gap-2">
          {blockedTables.hardcoded_blocked?.map((t) => (
            <span
              key={t}
              className="px-3 py-1.5 bg-red-50 border border-red-200 text-red-700 text-xs font-mono rounded-full"
            >
              {t}
            </span>
          ))}
        </div>
      </div>

      {/* Configurable */}
      <div>
        <h3 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
          <FontAwesomeIcon icon={faShieldAlt} className="text-orange-500" />
          Admin-Configured Blocked Tables
        </h3>

        {/* Add new block */}
        <div className="flex items-end gap-3 mb-4 p-4 bg-gray-50 rounded-xl border border-gray-200">
          <div className="flex-1">
            <label className="text-xs font-medium text-gray-600 block mb-1">Table Name</label>
            <input
              type="text"
              value={newBlockedTable}
              onChange={(e) => setNewBlockedTable(e.target.value)}
              placeholder="e.g. sensitive_data_table"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex-1">
            <label className="text-xs font-medium text-gray-600 block mb-1">Reason (optional)</label>
            <input
              type="text"
              value={newBlockedReason}
              onChange={(e) => setNewBlockedReason(e.target.value)}
              placeholder="Why block this table?"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <button
            onClick={handleAddBlocked}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition text-sm font-medium whitespace-nowrap"
          >
            <FontAwesomeIcon icon={faPlus} className="mr-1.5" />
            Block Table
          </button>
        </div>

        {blockedLoading ? (
          <div className="flex items-center justify-center py-8">
            <FontAwesomeIcon icon={faSpinner} spin className="text-blue-500" />
            <span className="ml-2 text-gray-500 text-sm">Loading...</span>
          </div>
        ) : blockedTables.configurable_blocked?.length === 0 ? (
          <div className="text-center py-8 text-gray-400 text-sm">
            No admin-configured blocked tables yet. Add one above.
          </div>
        ) : (
          <div className="space-y-2">
            {blockedTables.configurable_blocked?.map((bt) => (
              <div
                key={bt.id}
                className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200 hover:border-orange-300 transition"
              >
                <div>
                  <span className="font-mono text-sm font-semibold text-gray-800">{bt.table_name}</span>
                  {bt.reason && <span className="ml-3 text-xs text-gray-500">— {bt.reason}</span>}
                  <div className="text-xs text-gray-400 mt-0.5">
                    Blocked by {bt.blocked_by || "admin"} on{" "}
                    {bt.created_at ? new Date(bt.created_at).toLocaleString() : "N/A"}
                  </div>
                </div>
                <button
                  onClick={() => handleRemoveBlocked(bt.id, bt.table_name)}
                  className="px-3 py-1.5 text-xs bg-green-100 text-green-700 hover:bg-green-200 rounded-lg transition font-medium"
                >
                  Unblock
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  // ─────────────────────────────────────────────
  // AUDIT LOGS TAB
  // ─────────────────────────────────────────────
  const auditLastPage = Math.ceil(auditTotal / 20) || 1;

  const renderAuditTab = () => (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-gray-800">Update Audit Trail</h3>
        <div className="flex items-center gap-2">
          <input
            type="text"
            placeholder="Filter by table..."
            value={auditFilterTable}
            onChange={(e) => setAuditFilterTable(e.target.value)}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm w-48 focus:ring-2 focus:ring-blue-500"
          />
          <button
            onClick={() => {
              setAuditPage(1);
              fetchAuditLogs(1, auditFilterTable);
            }}
            className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 transition"
          >
            <FontAwesomeIcon icon={faSearch} />
          </button>
        </div>
      </div>

      {auditLoading ? (
        <div className="flex items-center justify-center py-12">
          <FontAwesomeIcon icon={faSpinner} spin className="text-blue-500 text-xl" />
          <span className="ml-2 text-gray-500">Loading logs...</span>
        </div>
      ) : auditLogs.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <FontAwesomeIcon icon={faHistory} className="text-4xl mb-3" />
          <p>No audit logs found.</p>
        </div>
      ) : (
        <>
          <div className="space-y-2">
            {auditLogs.map((log) => (
              <div
                key={log.id}
                className={`bg-white rounded-xl border overflow-hidden ${
                  log.update_mode === "revert"
                    ? "border-orange-200 bg-orange-50/30"
                    : "border-gray-200"
                }`}
              >
                <button
                  onClick={() => setExpandedAuditRow(expandedAuditRow === log.id ? null : log.id)}
                  className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition"
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <span
                      className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${
                        log.status === "success" ? "bg-green-500" : "bg-red-500"
                      }`}
                    />
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-sm font-semibold text-gray-800">{log.target_table}</span>
                        <span
                          className={`px-2 py-0.5 text-[10px] font-bold rounded-full uppercase ${
                            log.update_mode === "revert"
                              ? "bg-orange-100 text-orange-700"
                              : "bg-blue-100 text-blue-700"
                          }`}
                        >
                          {log.update_mode === "revert" ? (
                            <>
                              <FontAwesomeIcon icon={faUndo} className="mr-1" />
                              revert
                            </>
                          ) : (
                            log.update_mode
                          )}
                        </span>
                        <span className="text-xs text-gray-500">{log.affected_rows} row(s)</span>
                        {log.reference_audit_id && (
                          <span className="text-[10px] text-orange-600 font-medium">
                            (ref: #{log.reference_audit_id})
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-400 mt-0.5">
                        by {log.username || "—"} &middot;{" "}
                        {log.created_at ? new Date(log.created_at).toLocaleString() : "N/A"} &middot; IP:{" "}
                        {log.ip_address || "—"}
                      </div>
                    </div>
                  </div>
                  <FontAwesomeIcon
                    icon={expandedAuditRow === log.id ? faChevronUp : faChevronDown}
                    className="text-gray-400"
                  />
                </button>

                {expandedAuditRow === log.id && (
                  <div className="px-4 pb-4 border-t border-gray-100 space-y-3">
                    {log.update_mode === "revert" && log.reference_audit_id && (
                      <div className="mt-3 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                        <p className="text-xs text-orange-700">
                          <FontAwesomeIcon icon={faUndo} className="mr-1" />
                          This is a <strong>revert</strong> of audit log <strong>#{log.reference_audit_id}</strong>. The values below show what was restored.
                        </p>
                      </div>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                      <div>
                        <h5 className="text-xs font-bold text-gray-600 mb-1">Target Columns</h5>
                        <pre className="text-xs bg-gray-50 p-2 rounded-lg overflow-x-auto max-h-24">
                          {(() => {
                            try {
                              return JSON.stringify(JSON.parse(log.target_columns), null, 2);
                            } catch {
                              return log.target_columns;
                            }
                          })()}
                        </pre>
                      </div>
                      <div>
                        <h5 className="text-xs font-bold text-gray-600 mb-1">
                          {log.update_mode === "revert" ? "Reverted To (Old Values)" : "New Values"}
                        </h5>
                        <pre className="text-xs bg-gray-50 p-2 rounded-lg overflow-x-auto max-h-24">
                          {(() => {
                            try {
                              return JSON.stringify(JSON.parse(log.new_values), null, 2);
                            } catch {
                              return log.new_values;
                            }
                          })()}
                        </pre>
                      </div>
                      <div>
                        <h5 className="text-xs font-bold text-gray-600 mb-1">Filter Conditions</h5>
                        <pre className="text-xs bg-gray-50 p-2 rounded-lg overflow-x-auto max-h-24">
                          {(() => {
                            try {
                              return JSON.stringify(JSON.parse(log.filter_conditions), null, 2);
                            } catch {
                              return log.filter_conditions;
                            }
                          })()}
                        </pre>
                      </div>
                      <div>
                        <h5 className="text-xs font-bold text-gray-600 mb-1">
                          {log.update_mode === "revert" ? "Values Before Revert" : "Old Values (Snapshot)"}
                        </h5>
                        <pre className="text-xs bg-gray-50 p-2 rounded-lg overflow-x-auto max-h-24">
                          {(() => {
                            try {
                              const parsed = JSON.parse(log.old_values);
                              return JSON.stringify(parsed.length > 5 ? parsed.slice(0, 5) : parsed, null, 2);
                            } catch {
                              return log.old_values || "N/A";
                            }
                          })()}
                        </pre>
                      </div>
                    </div>
                    {log.error_message && (
                      <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                        <h5 className="text-xs font-bold text-red-600 mb-1">Error</h5>
                        <p className="text-xs text-red-700">{log.error_message}</p>
                      </div>
                    )}

                    {/* Revert Button — only for success, non-revert entries */}
                    {log.status === "success" && log.update_mode !== "revert" && (
                      <div className="flex justify-end pt-2 border-t border-gray-100">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRevert(log);
                          }}
                          disabled={revertLoading === log.id}
                          className="flex items-center gap-2 px-4 py-2 text-sm bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition font-medium disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                        >
                          {revertLoading === log.id ? (
                            <FontAwesomeIcon icon={faSpinner} spin />
                          ) : (
                            <FontAwesomeIcon icon={faUndo} />
                          )}
                          Revert to Old Values
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Pagination */}
          {auditTotal > 20 && (
            <div className="flex items-center justify-between mt-4">
              <span className="text-xs text-gray-500">
                Page {auditPage} of {auditLastPage} &middot; {auditTotal} total
              </span>
              <div className="flex gap-2">
                <button
                  disabled={auditPage <= 1}
                  onClick={() => {
                    const p = auditPage - 1;
                    setAuditPage(p);
                    fetchAuditLogs(p, auditFilterTable);
                  }}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-40 transition"
                >
                  <FontAwesomeIcon icon={faArrowLeft} className="mr-1" />
                  Prev
                </button>
                <button
                  disabled={auditPage >= auditLastPage}
                  onClick={() => {
                    const p = auditPage + 1;
                    setAuditPage(p);
                    fetchAuditLogs(p, auditFilterTable);
                  }}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-40 transition"
                >
                  Next
                  <FontAwesomeIcon icon={faArrowRight} className="ml-1" />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );

  // ─────────────────────────────────────────────
  // Step indicator
  // ─────────────────────────────────────────────
  const steps = [
    { num: 1, label: "Select Table" },
    { num: 2, label: "Columns & Values" },
    { num: 3, label: "Filters" },
    { num: 4, label: "Review" },
    { num: 5, label: "Done" },
  ];

  // ─────────────────────────────────────────────
  // MAIN RENDER
  // ─────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-sky-50 p-4 md:p-6">
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-6">
        <div className="flex items-center gap-3 mb-1">
          <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-xl flex items-center justify-center shadow-lg">
            <FontAwesomeIcon icon={faDatabase} className="text-white text-lg" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Dynamic Table Update</h1>
            <p className="text-sm text-gray-500">Admin Module &middot; Manage database records securely</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-7xl mx-auto mb-4">
        <div className="flex gap-1 bg-white rounded-xl border border-gray-200 p-1 shadow-sm w-fit">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition flex items-center gap-2 ${
                activeTab === tab.id
                  ? "bg-blue-600 text-white shadow-sm"
                  : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
              }`}
            >
              <FontAwesomeIcon icon={tab.icon} />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content area */}
      <div className="max-w-7xl mx-auto">
        {activeTab === "update" && (
          <>
            {/* Step indicator */}
            {currentStep < 5 && (
              <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-2">
                {steps.slice(0, 4).map((step, idx) => (
                  <React.Fragment key={step.num}>
                    {idx > 0 && (
                      <div
                        className={`h-0.5 w-8 flex-shrink-0 ${
                          currentStep > idx ? "bg-blue-500" : "bg-gray-200"
                        }`}
                      />
                    )}
                    <button
                      onClick={() => {
                        if (step.num < currentStep) setCurrentStep(step.num);
                      }}
                      disabled={step.num > currentStep}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition ${
                        currentStep === step.num
                          ? "bg-blue-600 text-white shadow-sm"
                          : currentStep > step.num
                          ? "bg-blue-100 text-blue-700 cursor-pointer hover:bg-blue-200"
                          : "bg-gray-100 text-gray-400 cursor-not-allowed"
                      }`}
                    >
                      <span
                        className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                          currentStep === step.num
                            ? "bg-white text-blue-600"
                            : currentStep > step.num
                            ? "bg-blue-600 text-white"
                            : "bg-gray-300 text-white"
                        }`}
                      >
                        {currentStep > step.num ? "✓" : step.num}
                      </span>
                      {step.label}
                    </button>
                  </React.Fragment>
                ))}
              </div>
            )}

            {/* Step content */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 mb-4">
              {currentStep === 1 && renderStep1()}
              {currentStep === 2 && renderStep2()}
              {currentStep === 3 && renderStep3()}
              {currentStep === 4 && renderStep4()}
              {currentStep === 5 && renderStep5()}
            </div>

            {/* Sticky action bar */}
            {currentStep >= 2 && currentStep <= 4 && (
              <div className="sticky bottom-0 bg-white/95 backdrop-blur border border-gray-200 rounded-2xl shadow-lg p-4 flex items-center justify-between">
                <button
                  onClick={() => {
                    if (currentStep === 2) {
                      setCurrentStep(1);
                      setSelectedTable(null);
                      setSelectedColumns([]);
                      setColumnValues({});
                      setNullFlags({});
                    } else {
                      setCurrentStep(currentStep - 1);
                    }
                  }}
                  className="flex items-center gap-2 px-4 py-2.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition font-medium text-gray-700"
                >
                  <FontAwesomeIcon icon={faArrowLeft} />
                  Back
                </button>

                <div className="flex items-center gap-3">
                  {currentStep === 2 && (
                    <button
                      disabled={selectedColumns.length === 0}
                      onClick={() => setCurrentStep(3)}
                      className="flex items-center gap-2 px-5 py-2.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      Next: Set Filters
                      <FontAwesomeIcon icon={faArrowRight} />
                    </button>
                  )}

                  {currentStep === 3 && (
                    <button
                      onClick={handlePreview}
                      disabled={previewLoading || filters.filter((f) => f.column).length === 0}
                      className="flex items-center gap-2 px-5 py-2.5 text-sm bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition font-medium disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      {previewLoading ? (
                        <FontAwesomeIcon icon={faSpinner} spin />
                      ) : (
                        <FontAwesomeIcon icon={faEye} />
                      )}
                      Preview Affected Rows
                    </button>
                  )}

                  {currentStep === 4 && previewData && (
                    <button
                      onClick={handleExecute}
                      disabled={executeLoading || (previewData?.total_affected || 0) === 0}
                      className="flex items-center gap-2 px-5 py-2.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition font-medium disabled:opacity-40 disabled:cursor-not-allowed shadow-lg"
                    >
                      {executeLoading ? (
                        <FontAwesomeIcon icon={faSpinner} spin />
                      ) : (
                        <FontAwesomeIcon icon={faPaperPlane} />
                      )}
                      Execute Update ({previewData?.total_affected || 0} rows)
                    </button>
                  )}
                </div>
              </div>
            )}
          </>
        )}

        {activeTab === "blocked" && (
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
            {renderBlockedTab()}
          </div>
        )}

        {activeTab === "audit" && (
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
            {renderAuditTab()}
          </div>
        )}
      </div>
    </div>
  );
};

export default DynamicTableUpdatePage;
